// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Pacing and failure handling for importing a stack of old estimates at once.
 *
 * Importing one job at a time is where somebody stops at three, and three is
 * not enough for any of the portfolio views to say something worth reading. So
 * the queue takes the whole folder — but it cannot simply loop, for two
 * reasons.
 *
 * The endpoint allows five calls a minute per user, so an unpaced run of ten
 * files fails on the sixth and leaves the person worse off than if they had
 * done it by hand. And the calls cost real money, so a run that hits the spend
 * ceiling has to stop rather than grind through eight more refusals.
 *
 * Both of those are decisions about what to do next, which is why they are
 * here as pure functions rather than buried in a component.
 */

/** What the server permits, mirrored from api/ai-import.js. */
export const RATE_LIMIT = 5;
export const RATE_WINDOW_MS = 60_000;

/**
 * How long to wait before the next call is allowed.
 *
 * Deliberately paces to just inside the server's own limit rather than
 * sprinting into a 429 and backing off: a retry after a refusal costs the same
 * wait and arrives having already shown the user a failure.
 */
export function msUntilAllowed(
  startedAt: number[],
  now: number,
  limit: number = RATE_LIMIT,
  windowMs: number = RATE_WINDOW_MS,
): number {
  const inWindow = startedAt.filter((t) => now - t < windowMs).sort((a, b) => a - b);
  if (inWindow.length < limit) return 0;
  // The oldest call in the window has to age out before another is allowed.
  const oldest = inWindow[inWindow.length - limit];
  return Math.max(0, oldest + windowMs - now);
}

export type FailureAction = "stop" | "retry" | "skip";

export interface FailureVerdict {
  action: FailureAction;
  /** Said to the user, so it has to be about their situation, not the HTTP. */
  reason: string;
}

/**
 * What a failed file means for the rest of the queue.
 *
 * The distinction that matters: a problem with THIS file should not cost the
 * person the other nine, and a problem with the account should not make them
 * watch nine more failures to find that out.
 */
export function classifyImportFailure(message: string): FailureVerdict {
  const m = String(message || "").toLowerCase();

  // Nothing further will succeed — the account, not the file.
  if (m.includes("credit balance") || m.includes("plans & billing")) {
    return { action: "stop", reason: "The Anthropic account is out of credits. Nothing further will import until it is topped up." };
  }
  if (m.includes("allowance") || m.includes("paused for this month")) {
    return { action: "stop", reason: "This month's AI allowance is used up. The remaining files were not attempted." };
  }
  if (m.includes("unauthorized") || m.includes("sign in") || m.includes("pending approval")) {
    return { action: "stop", reason: "Your session is no longer valid. Sign in again and restart the import." };
  }
  if (m.includes("premium feature")) {
    return { action: "stop", reason: "AI Import is not available on this plan." };
  }
  if (m.includes("not configured") || m.includes("server configuration")) {
    return { action: "stop", reason: "The importer is not configured on the server." };
  }

  // Worth another go in a moment.
  if (m.includes("rate limit") || m.includes("too many") || m.includes("429")) {
    return { action: "retry", reason: "Rate limited — waiting before the next file." };
  }
  if (m.includes("502") || m.includes("503") || m.includes("504") || m.includes("timed out") || m.includes("failed to connect")) {
    return { action: "retry", reason: "The service did not answer — trying once more." };
  }

  // This file is the problem. The rest of the stack is fine.
  return { action: "skip", reason: message || "Could not read this file." };
}

export type ItemStatus = "pending" | "working" | "done" | "failed" | "skipped";

export interface QueueItem {
  name: string;
  status: ItemStatus;
  /** Direct cost of what came back, for the eyeball check. */
  subtotal?: number;
  /** Lines the firm's catalog had no home for. */
  unmapped?: number;
  error?: string;
}

/** Plain-language state of the run, for a heading that is worth reading. */
export function queueSummary(items: QueueItem[]): string {
  const done = items.filter((i) => i.status === "done").length;
  const failed = items.filter((i) => i.status === "failed").length;
  const skipped = items.filter((i) => i.status === "skipped").length;
  const left = items.filter((i) => i.status === "pending" || i.status === "working").length;

  if (left > 0) {
    return `Imported ${done} of ${items.length}${failed ? `, ${failed} failed` : ""}…`;
  }
  if (done === 0) return failed || skipped ? "Nothing imported." : "Nothing to import.";

  const parts = [`Imported ${done} project${done === 1 ? "" : "s"}`];
  if (failed) parts.push(`${failed} failed`);
  if (skipped) parts.push(`${skipped} not attempted`);
  return parts.join(", ") + ".";
}

/**
 * Roughly how long a run will take, so the estimate on screen is honest about
 * the pacing rather than implying it is instant.
 */
export function estimateRunMs(fileCount: number, perFileMs = 12_000): number {
  if (fileCount <= 0) return 0;
  // The first batch goes straight out; every batch after it waits for the window.
  const waitBatches = Math.floor(Math.max(0, fileCount - 1) / RATE_LIMIT);
  return fileCount * perFileMs + waitBatches * RATE_WINDOW_MS;
}

export function humanDuration(ms: number): string {
  if (ms < 60_000) return `about ${Math.max(1, Math.round(ms / 1000))} seconds`;
  const mins = Math.round(ms / 60_000);
  return `about ${mins} minute${mins === 1 ? "" : "s"}`;
}
